import React from 'react';

import {
  Box,
  Display,
  DisplayProps,
  Flex,
  Heading,
  HeadingProps,
  Image,
  Paragraph,
  ParagraphProps,
} from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { SectionProps } from '@rover/kibble/patterns/Section/Section.common';
import Accordion from '@rover/react-lib/src/components/lists/Accordion';
import { AccordionItemProps } from '@rover/react-lib/src/components/lists/Accordion/Accordion.common';
import useRoverBrandContext from '@rover/react-lib/src/context/useRoverBrandContext';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import getBrandSpecificValue, {
  type BrandSpecificValueInput,
} from '@rover/shared/js/utils/getBrandSpecificValue';

import { FaqAccordionCollapsed, FaqAccordionExpanded } from './analyticsEvents';
import ciafDoodle from './faq-ciaf-doodle.svg?url';
import messages from './messages.png';

const DEFAULT_IMAGES: BrandSpecificValueInput = { rover: messages, catInAFlat: ciafDoodle };

// An FAQ accordion item. `faqId` is the FrequentlyAskedQuestion model id
export type FaqItem = AccordionItemProps & { faqId?: number };

// Accordion configured for FAQs
type FaqAccordionProps = { faqItems: FaqItem[] };
export const FaqAccordion: React.FC<FaqAccordionProps> = ({ faqItems }) => {
  const trackedFaqItems: AccordionItemProps[] = faqItems.map(({ faqId, onToggle, ...item }) => ({
    ...item,
    onToggle: (isOpen: boolean) => {
      const EventCtor = isOpen ? FaqAccordionExpanded : FaqAccordionCollapsed;
      emitAnalyticsEvent(new EventCtor({ faqId }));
      onToggle?.(isOpen);
    },
  }));

  return (
    <Accordion
      data-testid="faq-accordion"
      headingSize="300"
      defaultOpen="first"
      accordionItems={trackedFaqItems}
      itemSchemaProps={{
        itemScope: true,
        itemProp: 'mainEntity',
        itemType: 'https://schema.org/Question',
      }}
      bodySchemaProps={{
        itemScope: true,
        itemProp: 'acceptedAnswer',
        itemType: 'https://schema.org/Answer',
      }}
    />
  );
};

type HeaderVariant = 'display' | 'heading';

type FaqSectionHeaderProps = (DisplayProps | HeadingProps) & {
  /** Controls the typographic style of the header. Defaults to 'display'. */
  variant?: HeaderVariant;
};

type SubComponents = {
  Header: React.FC<FaqSectionHeaderProps>;
  Subheader: typeof Paragraph;
};

type FaqSectionProps = {
  faqItems: FaqItem[];
  imageSrc?: string | null;
  imageWidth?: number;
  imageDescription?: string;
} & SectionProps;

// FAQ Section
const FaqSection: React.FC<FaqSectionProps> & SubComponents = ({
  children,
  faqItems,
  imageSrc,
  imageWidth = 242,
  imageDescription = '',
  ...sectionProps
}: FaqSectionProps) => {
  const { brand } = useRoverBrandContext();
  const resolvedImageSrc =
    imageSrc === undefined ? getBrandSpecificValue(DEFAULT_IMAGES, brand) : imageSrc;

  return (
    <Section
      data-testid="faq-section"
      webProps={{ itemScope: true, itemType: 'https://schema.org/FAQPage' }}
      {...sectionProps}
    >
      <Flex flexWrap={['wrap', 'nowrap']} gap="16x" justifyContent="center">
        <Box flex="2 1 80%">
          {children}
          <FaqAccordion faqItems={faqItems} />
        </Box>

        {resolvedImageSrc && (
          <Box
            flexBasis={`${imageWidth}px`}
            flexShrink={['1', '1', '0']} // don't allow the image to shrink on desktop
            textAlign="center"
            maxWidth={`${imageWidth}px`}
            display={['none', 'block']}
          >
            <Image
              src={resolvedImageSrc}
              description={imageDescription}
              height="auto"
              width="100%"
            />
          </Box>
        )}
      </Flex>
    </Section>
  );
};

// header and subheader for the section
FaqSection.Header = ({ children, variant = 'display', ...headerProps }: FaqSectionHeaderProps) => {
  if (variant === 'heading') {
    return (
      <Heading size="400" mb="4x" {...(headerProps as HeadingProps)}>
        {children}
      </Heading>
    );
  }

  return (
    <Display size="500" mb="4x" {...(headerProps as DisplayProps)}>
      {children}
    </Display>
  );
};

FaqSection.Subheader = ({ children, ...paragraphProps }: ParagraphProps) => (
  <Paragraph webProps={{ maxWidth: 'none' }} size="200" marginBottom="6x" {...paragraphProps}>
    {children}
  </Paragraph>
);

export default FaqSection;
